/**
 * IndexedDB snapshot of /api/notifications/preferences for the Master Service Worker.
 *
 * Mirrors packages/frontend/src/lib/notificationPrefsSync.ts for the master
 * surface. The SW (packages/frontend-master/public/sw.js) reads from the
 * 'notif-prefs' key in the 'easyflow-prefs' database before calling
 * showNotification so it can enforce per-category push toggles and quiet hours
 * without a backend round-trip on every push.
 *
 * When the master app runs on a dedicated subdomain its IndexedDB is
 * origin-scoped and cannot share the tenant frontend's snapshot — this module
 * ensures the master origin always has its own up-to-date copy.
 *
 * Call sites:
 *  - AuthContext: after session restore (checkSession) and after login().
 *  - AuthContext: on logout (clearNotificationPrefsSnapshot).
 *  - NotificationsSettingsTab (if ever added to master): after PUT /preferences.
 *
 * Failures are non-fatal: the SW fail-opens when no snapshot is found.
 */

import { apiClient } from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';

const DB_NAME = 'easyflow-prefs';
const DB_VERSION = 1;
const STORE = 'kv';
const KEY = 'notif-prefs';

interface PrefsSnapshot {
  categories: Record<string, { in_app: boolean; push: boolean; email: boolean; sound: boolean }>;
  quiet_hours: { enabled: boolean; start_time: string | null; end_time: string | null };
  /**
   * Tenant slug derived from the master JWT — written so the SW can compose
   * tenant-scoped API URLs (e.g. /api/<slug>/notifications/:id/dismissed).
   */
  tenantSlug: string | null;
  updatedAt: number;
}

interface PreferencesPayload {
  categories?: Array<{
    category: string;
    in_app_enabled?: boolean;
    push_enabled?: boolean;
    email_enabled?: boolean;
    sound_enabled?: boolean;
  }>;
  quiet_hours?: { enabled?: boolean; start_time?: string | null; end_time?: string | null };
}

function readTenantSlugFromMasterJwt(): string | null {
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    if (!token) return null;
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const payload = JSON.parse(atob(payloadPart));
    return payload.tenantSlug || payload.tenant_slug || null;
  } catch {
    return null;
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function writeSnapshot(snapshot: PrefsSnapshot): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(snapshot, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

function notifyServiceWorker(): void {
  try {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      const sw = reg && (reg.active || reg.waiting || reg.installing);
      if (sw) sw.postMessage({ type: 'NOTIF_PREFS_UPDATED' });
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

/**
 * Fetch the current preference matrix and persist it to IndexedDB on the
 * master origin so the master SW can read it on the next push event.
 * Resolves to `true` on success, `false` on any failure. Never throws.
 */
export async function syncNotificationPrefsToMasterSW(): Promise<boolean> {
  try {
    const res = await apiClient.get<PreferencesPayload>('/notifications/preferences');
    const payload: PreferencesPayload | undefined = res?.data;
    if (!payload || !Array.isArray(payload.categories)) return false;

    const categories: PrefsSnapshot['categories'] = {};
    for (const row of payload.categories) {
      if (!row || typeof row.category !== 'string') continue;
      categories[row.category] = {
        in_app: row.in_app_enabled !== false,
        push: row.push_enabled !== false,
        email: row.email_enabled === true,
        sound: row.sound_enabled !== false,
      };
    }
    const qh = payload.quiet_hours || {};
    const snapshot: PrefsSnapshot = {
      categories,
      quiet_hours: {
        enabled: !!qh.enabled,
        start_time: qh.start_time || null,
        end_time: qh.end_time || null,
      },
      tenantSlug: readTenantSlugFromMasterJwt(),
      updatedAt: Date.now(),
    };
    await writeSnapshot(snapshot);
    notifyServiceWorker();
    return true;
  } catch (err) {
    console.warn('[master notif-prefs] snapshot sync failed:', (err as Error)?.message || err);
    return false;
  }
}

/**
 * Wipe the snapshot on logout so the SW doesn't apply a stale gating matrix
 * for the previous session on shared devices.
 */
export async function clearNotificationPrefsSnapshot(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    db.close();
    notifyServiceWorker();
  } catch {
    /* ignore */
  }
}
